import React, { useEffect, useRef, useState } from 'react';
import seedrandom from 'seedrandom';
import _, { find, flatten, without } from 'lodash';
import { browserName, isMobile } from 'react-device-detect';
import { createPortal } from 'react-dom';

interface Props {
  answer: any;
  prompt: any;
}

const DraggableInlineInputs: React.FC<Props> = (props) => {
  const [activeDraggableText, setActiveDraggableText] = useState(null);
  const activeDraggableTextRef = useRef(null);

  // Drag and drop does not work well on mobile Safari (it thinsk you are selecting a word to look
  // it up). To handle that case we instead use a tap to select, then tap place in the blank
  // interaction. Update the question instructions to reflect this by using the mobile
  // instructions.
  useEffect(() => {
    if (browserName !== 'Safari') return;

    const answerInstructions = document.querySelector('.answer-instructions');
    if (!answerInstructions) return;

    // Find and remove the child with "hidden-xs" class
    const hiddenElement = answerInstructions.querySelector('.hidden-xs');
    if (hiddenElement) {
      hiddenElement.remove();
    }

    // Find and remove the "visible-xs" class from the other span
    const visibleElement = answerInstructions.querySelector('.visible-xs');
    if (visibleElement) {
      visibleElement.classList.remove('visible-xs');
    }
  }, []);

  // Use a seeded shuffle so that the answer choice order will randomized, but consistent
  const shuffleSeeded = (array, seed) => {
    seedrandom(seed, { global: true });
    const _lodash = _.runInContext();
    return _lodash.shuffle(array);
  };

  const answerChoices = useRef(
    shuffleSeeded(
      flatten(
        props.prompt.answerChoiceGroups.map((answerChoiceGroup) => (
          answerChoiceGroup.answerChoices.map((choice) => (
            { id: choice.id, text: choice.text }
          ))
        ))
      ),
      `${props.prompt.id}`
    )
  );

  const [inlineInputs, setInlineInputs] = useState(null);
  const [values, setValues] = useState(
    props.answer.parts.length ? (
      props.answer.parts.map((part) => part.text)
    ) : (
      Array(props.prompt.answerChoiceGroups.length).fill(null)
    )
  );

  useEffect(() => {
    activeDraggableTextRef.current = activeDraggableText;
  }, [activeDraggableText]);

  useEffect(() => {
    const targetInputs = document.getElementsByClassName('drag-target-inline');
    setInlineInputs(targetInputs);

    // Sometimes when dragging, the user might also inadvertently select some surrounding text.
    // All the selected text will go with the cursor as the user drags which is confusing visually.
    // Prevent this by not allowing text in the sentence to be selected.
    targetInputs[0].parentElement.style['user-select'] = 'none';
  }, []);

  useEffect(() => {
    const clearActiveDraggableText = (event) => {
      if (!activeDraggableTextRef.current) return;
      if (event.target.className === 'draggable-text') return;
      if (event.target.className === 'drag-target') return;

      setValues((currentValues) => (
        currentValues.map((value) => value === activeDraggableTextRef.current ? null : value)
      ));
      setActiveDraggableText(null);
    };

    document.addEventListener('click', clearActiveDraggableText);

    return () => {
      document.removeEventListener('click', clearActiveDraggableText);
    }
  }, []);

  const onValueChanged = (newValue, idx) => {
    // Remove the value from any previosuly assigned slots
    const updatedValues = values.map((value) => newValue === value ? null : value);
    updatedValues[idx] = newValue;
    setValues(updatedValues);
  };

  return (
    <>
      {
        props.prompt.answerChoiceGroups.map((answerChoiceGroup, idx) => {
          const inlineInput = (inlineInputs || [])[idx];

          return (
            <div key={idx} className="answer_input">
              {
                inlineInput && createPortal(
                  props.answer.isGraded ? (
                    <GradedInput
                      answerChoiceGroup={answerChoiceGroup}
                      value={values[idx]}
                    />
                  ) : (
                    <DragTargetInput
                      acceptPlace={!!activeDraggableText}
                      active={values[idx] === activeDraggableText}
                      answerChoiceGroupId={answerChoiceGroup.id}
                      onChange={(newValue) => onValueChanged(newValue, idx)}
                      onTapToPlace={() => {
                        onValueChanged(activeDraggableText, idx);
                        setActiveDraggableText(null);
                      }}
                      onTapToSelect={() => setActiveDraggableText(values[idx])}
                      value={values[idx]}
                    />
                  ),
                  inlineInput
                )
              }
            </div>
          );
        })
      }

      <div
        className="d-flex flex-wrap justify-content-center u-margin-V-l"
        style={{ columnGap: 10, rowGap: 5 }}
      >
        {
          answerChoices.current.map((choice: any) => (
            <DraggableText
              key={choice.id}
              active={choice.text === activeDraggableText}
              isDraggable={!props.answer.isGraded}
              inUse={values.indexOf(choice.text) !== -1}
              onTapToSelect={() => setActiveDraggableText(choice.text)}
              value={choice.text}
            >
              {choice.text}
            </DraggableText>
          ))
        }
      </div>
    </>
  );
};

interface DraggableTextProps {
  active: boolean;
  isDraggable: boolean;
  inUse: boolean;
  onDragEnd?: (event) => void;
  onTapToSelect?: (event) => void;
  value: string;
}

const DraggableText: React.FC<DraggableTextProps> = (props) => {
  let backgroundColor = 'inherit';
    if (props.inUse) {
      backgroundColor = 'rgb(239, 234, 231)';
    } else if (props.active) {
      backgroundColor = '#d3c7de';
    }

  const dragStart = (e) => {
    e.dataTransfer.setData('draggable', true);
    e.dataTransfer.setData('text/plain', props.value);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleClick = (event) => {
    if (useTapToSelect()) {
      props.onTapToSelect(event);
    }
  };

  const useTapToSelect = () => {
    return isMobile || browserName === 'Safari';
  };

  return (
    <span
      className="draggable-text"
      draggable={props.isDraggable && !useTapToSelect() && !props.inUse}
      onClick={handleClick}
      onDragEnd={props.onDragEnd}
      onDragStart={dragStart}
      style={{
        backgroundColor,
        cursor: props.isDraggable && !props.inUse ? 'move' : 'default',
        padding: '5px 10px'
      }}
    >
      {props.value}
    </span>
  );
};

interface DragTargetInputProps {
  acceptPlace: boolean;
  active: boolean;
  answerChoiceGroupId: number;
  onChange: (value: string) => void;
  onTapToPlace?: (event) => void;
  onTapToSelect?: (event) => void;
  value: string;
}

const DragTargetInput: React.FC<DragTargetInputProps> = (props) => {
  const dragEnd = (e) => {
    if (e.dataTransfer.dropEffect === 'none') {
      props.onChange(null);
    }
  };

  const useTapToPlace = () => {
    return isMobile || browserName === 'Safari'
  };

  const onDrop =(e) => {
    if (!e.dataTransfer.getData('draggable')) return;

    const newValue = e.dataTransfer.getData('text/plain');
    props.onChange(newValue);
  };

  return (
    <>
      <span
        className="drag-target"
        onClick={useTapToPlace() && props.acceptPlace ? props.onTapToPlace : () => {}}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          borderBottom: '1px solid #4b504e',
          display: 'inline-block',
          marginBottom: 5,
          minHeight: 25,
          minWidth: props.value ? 'auto' : 136
        }}
      >
        {
          props.value ? (
            <DraggableText
              active={props.active}
              isDraggable={!useTapToPlace()}
              inUse={false}
              onDragEnd={dragEnd}
              onTapToSelect={props.onTapToSelect}
              value={props.value}
            />
          ) : (
            <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />
          )
        }
      </span>

      {
        props.value && (
          <input
            name={`answer[parts_val][${props.answerChoiceGroupId}][text][]`}
            type="hidden"
            value={props.value}
          />
        )
      }
    </>
  )
};

interface GradedInputProps {
  answerChoiceGroup: any;
  value: string;
}

const GradedInput: React.FC<GradedInputProps> = (props) => {
  const correctValue = props.answerChoiceGroup.answerChoices.find((choice) => (
    choice.isCorrect
  )).text;
  const isCorrect = props.value === correctValue;

  return (
    <span
      style={{
        borderBottom: '1px solid #4b504e',
        display: 'inline-block',
        marginBottom: 5,
        minHeight: 25
      }}
    >
      {
        !isCorrect && (
          <span style={{ backgroundColor: '#9bdcae', fontWeight: 'bold' }}>
            {correctValue}
          </span>
        )
      }

      <span
        style={{
          backgroundColor: isCorrect ? '#9bdcae' : '#ecacae',
          color: isCorrect ? 'inherit' : '#D9595D',
          fontWeight: 'bold',
          marginLeft: isCorrect ? 0 : 2,
          textDecoration: isCorrect ? 'none' : 'line-through'
        }}
      >
        {props.value || <span dangerouslySetInnerHTML={{ __html: '&nbsp;&nbsp;&nbsp;&nbsp;' }} />}
      </span>
    </span>
  )
};

export default DraggableInlineInputs;
